import React, { useEffect, useState } from "react";
import { Button, Checkbox, DatePicker, Input, Modal, Select, message } from "antd";
import { ArrowLeftOutlined, ArrowRightOutlined, CheckOutlined, ShopOutlined } from "@ant-design/icons";

import { getTaskAssignees, markOfficeDormant, markPracticeDormant } from "./controller";
import LeadTagPicker from "./LeadTagPicker";
import "./dormant.css";

/**
 * Mark a practice (or one office) dormant — three short steps, one request.
 *
 * Dormant is per OFFICE; a practice reads dormant only when every office is.
 * The flow therefore starts by asking which offices, then tags the practice's
 * LEAD (the lead outlives the offices' activity and is what we come back to
 * with a new offer later), then requires a note so the next person to open the
 * office knows why it went quiet. The server applies flag + tags + note
 * atomically, so there is no half-done state if one of them fails.
 *
 * Steps are gated (unlike the breakdown intake): each answer is short, and the
 * order is the order the question makes sense in.
 *
 * `target`: { kind: "practice" | "office", id, name, offices: [{id, name, dormant}] }
 * `onDone(result)` receives the server's {offices, practices, applied}.
 */

const STEPS = [
  { key: "offices", label: "Which offices" },
  { key: "tag", label: "Tag the lead" },
  { key: "note", label: "Leave a note" },
];

export default function MarkDormantWizard({ open, target, onClose, onDone }) {
  const [stepIndex, setStepIndex] = useState(0);
  const [checked, setChecked] = useState([]);
  const [reason, setReason] = useState("");
  const [tagIds, setTagIds] = useState([]);
  const [newTags, setNewTags] = useState([]);
  const [noteBody, setNoteBody] = useState("");
  const [isTask, setIsTask] = useState(false);
  const [dueDate, setDueDate] = useState(null);
  const [assigneeId, setAssigneeId] = useState(null);
  const [assignees, setAssignees] = useState([]);
  const [busy, setBusy] = useState(false);

  const isPractice = !!target && target.kind === "practice";
  const offices = (target && Array.isArray(target.offices) ? target.offices : []).map((o) => ({
    id: o.id, name: o.name, dormant: !!o.dormant,
  }));

  // Fresh state every time the wizard opens for a (possibly different) target.
  // Every office that is not already dormant starts checked — the common case
  // is "the whole practice went quiet", and unticking one is cheaper than
  // ticking many.
  useEffect(() => {
    if (!open) return;
    setStepIndex(0);
    setChecked(offices.filter((o) => !o.dormant).map((o) => o.id));
    setReason("");
    setTagIds([]);
    setNewTags([]);
    setNoteBody("");
    setIsTask(false);
    setDueDate(null);
    setAssigneeId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, target && target.kind, target && target.id]);

  useEffect(() => {
    if (!open) return;
    getTaskAssignees()
      .then((r) => {
        if (r.status >= 200 && r.status < 300 && Array.isArray(r.json)) setAssignees(r.json);
      })
      .catch(() => {});
  }, [open]);

  const step = STEPS[stepIndex];
  const selectable = offices.filter((o) => !o.dormant);
  const nothingToDo = isPractice && selectable.length === 0;
  const canLeaveOffices = isPractice ? checked.length > 0 : true;
  // The note is the one thing we insist on; a task without a date or assignee
  // is still a task worth seeing.
  const canFinish = noteBody.trim().length > 0;

  const toggle = (id, on) =>
    setChecked((prev) => (on ? prev.concat(prev.indexOf(id) === -1 ? [id] : []) : prev.filter((x) => x !== id)));

  const finish = async () => {
    if (!target) return;
    if (!noteBody.trim()) {
      message.error("Write a note before finishing.");
      return;
    }
    const body = {
      reason: reason.trim(),
      tag_ids: tagIds,
      new_tags: newTags,
      note: {
        body: noteBody.trim(),
        is_task: isTask,
        due_date: isTask && dueDate ? dueDate.format("YYYY-MM-DD") : null,
        assignee_id: isTask ? assigneeId : null,
      },
    };
    setBusy(true);
    const res = isPractice
      ? await markPracticeDormant(target.id, Object.assign({ office_ids: checked }, body))
      : await markOfficeDormant(target.id, body);
    setBusy(false);
    if (res.status >= 200 && res.status < 300) {
      const result = res.json || {};
      const n = Array.isArray(result.offices)
        ? result.offices.filter((o) => o.dormant).length || result.offices.length
        : isPractice ? checked.length : 1;
      message.success(`${n} office${n === 1 ? "" : "s"} marked dormant`);
      onDone && onDone(result);
      onClose && onClose();
    } else {
      message.error((res.json && res.json.detail) || "Couldn't mark dormant.");
    }
  };

  const body = () => {
    if (step.key === "offices") {
      return (
        <div>
          {isPractice ? (
            <>
              <p className="crmd-wiz-lead">
                Dormant is set per office. Untick any office that is still active.
              </p>
              {offices.length ? (
                <div className="crmd-office-list">
                  {offices.map((o) => (
                    <label key={o.id} className={`crmd-office-row${o.dormant ? " is-disabled" : ""}`}>
                      <Checkbox
                        disabled={o.dormant}
                        checked={o.dormant || checked.indexOf(o.id) !== -1}
                        onChange={(e) => toggle(o.id, e.target.checked)}
                      />
                      <span>{o.name}</span>
                      {o.dormant ? <span className="crm-sub">Already dormant</span> : null}
                    </label>
                  ))}
                </div>
              ) : (
                <div className="crm-empty">This practice has no offices to mark.</div>
              )}
              {nothingToDo && offices.length ? (
                <p className="crmd-wiz-lead" style={{ marginTop: 12 }}>
                  Every office is already dormant — there is nothing left to mark.
                </p>
              ) : null}
            </>
          ) : (
            <>
              <p className="crmd-wiz-lead">Only this office is affected; the rest of the practice stays as it is.</p>
              <div className="crmd-office-single">
                <ShopOutlined />
                <span>{target ? target.name : ""}</span>
              </div>
            </>
          )}

          <div className="crmd-wiz-label">Reason (optional)</div>
          <Input
            maxLength={255}
            showCount
            placeholder='e.g. "Trial ended, not proceeding"'
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
      );
    }
    if (step.key === "tag") {
      return (
        <div>
          <p className="crmd-wiz-lead">
            The practice's lead keeps these tags after the offices go quiet, so we can
            find it again when there is a new offer worth sending.
          </p>
          <div className="crmd-wiz-label">Tags</div>
          <LeadTagPicker
            value={tagIds}
            onChange={({ tagIds: ids, newTags: names }) => { setTagIds(ids); setNewTags(names); }}
            allowCreate
          />
          <div className="crmd-wiz-hint" style={{ marginTop: 8 }}>
            Optional. Type a name that does not exist yet to create it.
          </div>
        </div>
      );
    }
    return (
      <div>
        <p className="crmd-wiz-lead">
          Why did this go dormant, and is there anything to follow up on? The note lands
          on the office's Notes tab where the next person will look.
        </p>
        <Input.TextArea
          rows={4}
          autoFocus
          placeholder="e.g. Trial ended in June; owner said budget review in Q1 — try again then."
          value={noteBody}
          onChange={(e) => setNoteBody(e.target.value)}
        />
        <div className="crmd-task-row">
          <Checkbox checked={isTask} onChange={(e) => setIsTask(e.target.checked)}>
            Make this a task
          </Checkbox>
          {isTask ? (
            <>
              <DatePicker
                size="small"
                placeholder="Due date"
                value={dueDate}
                onChange={setDueDate}
                style={{ width: 140 }}
                popupStyle={{ zIndex: 10060 }}
              />
              <Select
                size="small"
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Assign to"
                value={assigneeId}
                onChange={setAssigneeId}
                style={{ minWidth: 170 }}
                dropdownStyle={{ zIndex: 10060 }}
                options={assignees.map((a) => ({ value: a.id, label: a.name || a.email }))}
              />
            </>
          ) : null}
        </div>
      </div>
    );
  };

  const last = stepIndex === STEPS.length - 1;
  const nextDisabled = step.key === "offices" ? !canLeaveOffices : false;

  return (
    <Modal
      title={target ? `Mark dormant · ${target.name}` : "Mark dormant"}
      open={!!open}
      onCancel={busy ? undefined : onClose}
      footer={null}
      width={620}
      // Opens from table rows AND from inside the detail drawers.
      zIndex={10050}
      destroyOnClose
      maskClosable={!busy}
    >
      <div className="crmd-wiz-steps" aria-label="Steps">
        {STEPS.map((s, i) => {
          const done = i < stepIndex;
          return (
            <div
              key={s.key}
              className={`crmd-wiz-step${i === stepIndex ? " is-active" : ""}${done ? " is-done" : ""}`}
              aria-current={i === stepIndex ? "step" : undefined}
            >
              <span className="crmd-wiz-num">{done ? <CheckOutlined /> : i + 1}</span>
              <span>{s.label}</span>
            </div>
          );
        })}
      </div>

      <div className="crmd-wiz-body">{body()}</div>

      <div className="crmd-wiz-foot">
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <span className="crmd-spacer" />
        {step.key === "offices" && isPractice && !canLeaveOffices && !nothingToDo ? (
          <span className="crmd-wiz-hint">Choose at least one office</span>
        ) : null}
        <Button
          icon={<ArrowLeftOutlined />}
          disabled={stepIndex === 0 || busy}
          onClick={() => setStepIndex(Math.max(0, stepIndex - 1))}
        >
          Back
        </Button>
        {last ? (
          <Button type="primary" loading={busy} disabled={!canFinish} onClick={finish}>
            Finish
          </Button>
        ) : (
          <Button
            type="primary"
            disabled={nextDisabled || nothingToDo}
            onClick={() => setStepIndex(Math.min(STEPS.length - 1, stepIndex + 1))}
          >
            Next <ArrowRightOutlined />
          </Button>
        )}
      </div>
    </Modal>
  );
}
